import { autoinject, bindingMode } from 'aurelia-framework';
import { ComponentBase, SortDirection, Sorting } from 'app/shared/lib/essette-site';
import { bindable } from 'aurelia-typed-observable-plugin';
import { apiCall } from 'app/shared/lib/api/api';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { CaseService } from 'app/cm/services/case-service';
import { ICustomFieldTargetColumnsContainer, ICustomFieldTargetColumnsSettings } from 'app/shared/lib/custom-field-definitions';
import { CustomFieldContext } from 'app/core/lib/custom-field-context';
import * as CM from 'app/cm/models/essette-api';

@autoinject()
export class ReferralDetailsCustomElement extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable.stringNull({ defaultBindingMode: bindingMode.toView, changeHandler: 'reload' })
    protected caseId: string | null = null;

    protected referralCriteria: Map<string, Array<CM.ReferralCriteria>> = new Map();
    protected customFieldTargetColumns: ICustomFieldTargetColumnsContainer | null = null;

    protected case: CM.Case | null = null;
    protected checkouts: CM.CaseCheckoutHistory[] = [];
    protected isCMOfflineEnabled: boolean = false;


    ////****************** Element References ***************************************////


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly service: CaseService,
        private readonly customFieldContext: CustomFieldContext,
        ...rest: any[]) {
        super(...rest);
    }

    protected bind(): void {
        this.isCMOfflineEnabled = this.env.config.integrations.includes('cm-offline');
        this.loadCustomFieldTargets();
        if(this.caseId) { this.reload(this.caseId); }
    }


    ////****************** Events ***************************************************////
    public async reload(caseId: string | null): Promise<void> {
        if(caseId) {
            this.loadReferralDetails(caseId);
            if(this.isCMOfflineEnabled) { this.loadCaseCheckouts(caseId); }
        }
    }


    ////****************** Functions ************************************************////
    private joinVerificationCriteriaToReferralTypes(): void {
        this.referralCriteria.clear();
        if(!(this.case?.referral?.types && this.case.referral.types.length)) return;
        this.case.referral.types.forEach(type => {
            if(!(this.case?.referral?.verification?.criteria && this.case.referral.verification.criteria.length) || !type.id) return;
            const verificationCriteria = this.case.referral.verification.criteria.filter(c => c.type?.id == type.id);
            this.referralCriteria.set(type.id, verificationCriteria);
        });
    }


    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadCustomFieldTargets(): Promise<void> {
        const settings: ICustomFieldTargetColumnsSettings = {
            definitionCount: 10,
            filterInactive: true,
            sortByLabel: true
        };
        this.customFieldTargetColumns = await this.customFieldContext.getCustomFieldTargetColumns('CM.Case', settings);
    }

    @apiCall
    private async loadReferralDetails(caseId: string): Promise<void> {
        const includes: string[] = [
            'assignedTo',
            'custom',
            'history.assignments',
            'metadata.created.byUser',
            'referral',
            'referral.destination',
            'referral.metadata.created.byUser',
            'referral.reason',
            'referral.source',
            'referral.types',
            'referral.verification.criteria'
        ];

        const fields: string[] = [
            'assignedTo.description',
            'custom.text.*',
            'custom.lookup.*',
            'history.assignments.description',
            'history.assignments.metadata.started.timestamp',
            'referral.comments',
            'referral.destination.description',
            'referral.metadata.created.byUser.description',
            'referral.metadata.created.timestamp',
            'referral.reason.description',
            'referral.source.description',
            'referral.types.description',
            'referral.verification.criteria',
            'referral.verification.criteria.description',
            'referral.verification.criteria.type.*'
        ]

        try {
            this.state.beginLoad();
            const response = await this.service.loadCase(caseId, includes, fields);
            this.case = response.results[0];
            this.joinVerificationCriteriaToReferralTypes();
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadCaseCheckouts(caseId: string): Promise<void> {
        const sorting = new Sorting();
        sorting.add({
            reference: 'metadata.started.timestamp',
            direction: SortDirection.Ascending
        });

        const options: Partial<IQueryLoadOptions> = {
            all: true,
            sorting: sorting
        };
        const includes: string[] = ['metadata.started.byUser'];
        try {
            this.state.beginLoad();

            const response = await this.service.loadCaseCheckoutHistory(caseId, options, includes);
            this.checkouts = response.results;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }
}
